import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  Edit,
  Delete,
  Add,
  Schedule,
  TrendingUp,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
} from "@mui/icons-material";
import { adminService } from "../api/services";
import { safeMap, safeArray } from "../utils/validation";
import AutoGenDebugPanel from "./AutoGenDebugPanel";
import AutoGenControls from "./AutoGenControls";

const AutoGenerationConfig = ({ categories }) => {
  const [configs, setConfigs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    categories: [],
    publish_time: "09:00",
    is_active: true,
    max_posts_per_day: 1,
    language: "es",
    tone: "viral",
    style: "emotional",
    niche: "trending",
    // ✅ NUEVOS CAMPOS AÑADIDOS
    auto_publish: false, // Publicar automáticamente
    share_on_social: false, // Compartir en redes
    use_search: false, // ✅ Búsqueda web
    image_source: 'generate', // ✅ Fuente de imágenes
    // ✅ NUEVO CAMPO: Tópico específico
    topic: "", // Tópico específico para búsqueda
  });

  const tones = [
    { value: "viral", label: "Viral y Clickbait" },
    { value: "professional", label: "Profesional" },
    { value: "emotional", label: "Emocional" },
    { value: "casual", label: "Casual" },
  ];

  const styles = [
    { value: "emotional", label: "Empático y Emocional" },
    { value: "authoritative", label: "Autoritativo" },
    { value: "storytelling", label: "Narrativo" },
    { value: "list", label: "Listas y Tips" },
  ];

  const niches = [
    { value: "trending", label: "Tendencias Actuales" },
    { value: "evergreen", label: "Contenido Evergreen" },
    { value: "breaking", label: "Noticias de Última Hora" },
    { value: "howto", label: "Guías y Tutoriales" },
  ];

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await adminService.getAutoConfigs();
      const configsArray = safeArray(response.data);
      setConfigs(configsArray);
    } catch (err) {
      console.error("❌ [DEBUG] Error fetching configs:", err);
      console.error("❌ [DEBUG] Error response:", err.response?.data);
      setError("Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        name: config.name,
        categories: config.categories.map((c) => c.id),
        publish_time: config.publish_time,
        is_active: config.is_active,
        max_posts_per_day: config.max_posts_per_day,
        language: config.language,
        tone: config.tone || "viral",
        style: config.style || "emotional",
        niche: config.niche || "trending",
        // ✅ Cargar nuevos campos al editar
        auto_publish: config.auto_publish || false,
        share_on_social: config.share_on_social || false,
        use_search: config.use_search || false,
        image_source: config.image_source || 'generate',
        // ✅ Cargar tópico si existe
        topic: config.topic || "",
      });
    } else {
      setEditingConfig(null);
      setFormData({
        name: "",
        categories: [],
        publish_time: "09:00",
        is_active: true,
        max_posts_per_day: 1,
        language: "es",
        tone: "viral",
        style: "emotional",
        niche: "trending",
        // ✅ Valores iniciales para nuevos campos
        auto_publish: false,
        share_on_social: false,
        use_search: false,
        image_source: 'generate',
        // ✅ Valor inicial para tópico
        topic: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // ✅ LÓGICA DE SUBMIT MODIFICADA
      const submitData = {
        ...formData,
        auto_publish: formData.auto_publish,
        share_on_social: formData.share_on_social,
        use_search: formData.use_search,
        image_source: formData.image_source,
        // ✅ Incluir el tópico en los datos enviados
        topic: formData.topic || null,
        // Asegura que las categorías sean IDs válidos
        categories: formData.categories.includes('any')
          ? categories.map(c => c.id)
          : formData.categories
      };

      if (editingConfig) {
        await adminService.updateAutoConfig(editingConfig.id, submitData);
      } else {
        const response = await adminService.createAutoConfig(submitData);
      }

      // 🚀 CORRECCIÓN CLAVE: Refrescar la lista después de la operación exitosa
      await fetchConfigs();

      setSuccess(
        editingConfig
          ? "Configuración actualizada exitosamente"
          : "Configuración creada exitosamente"
      );
      handleCloseDialog();
    } catch (err) {
      console.error("❌ [DEBUG] Error in handleSubmit:", err);
      console.error("❌ [DEBUG] Error response:", err.response?.data);
      setError(err.response?.data?.error || "Error al guardar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await adminService.updateAutoConfig(config.id, {
        is_active: !config.is_active,
      });
      setSuccess(`Configuración ${!config.is_active ? "activada" : "pausada"}`);
      fetchConfigs();
    } catch (err) {
      setError("Error al actualizar configuración");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de eliminar esta configuración?")) {
      try {
        await adminService.deleteAutoConfig(id);
        setSuccess("Configuración eliminada");
        fetchConfigs();
      } catch (err) {
        setError("Error al eliminar configuración");
      }
    }
  };

  const handleExecuteNow = async (config) => {
    setExecuting({ ...executing, [config.id]: true });
    try {
      await adminService.executeAutoGeneration(config.id);
      setSuccess(`Generación ejecutada para ${config.name}`);
    } catch (err) {
      setError("Error ejecutando generación");
    } finally {
      setExecuting({ ...executing, [config.id]: false });
    }
  };

  const getStatusIcon = (config) => {
    if (executing[config.id]) {
      return <CircularProgress size={20} />;
    }
    return config.is_active ? (
      <CheckCircle color="success" />
    ) : (
      <Pause color="disabled" />
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6">
          Configuración de Generación Automática
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Configuración
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Configuraciones Existentes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuraciones Activas
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Estado</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Tópico/Categorías</TableCell>
                        <TableCell>Horario</TableCell>
                        <TableCell>Configuración</TableCell>
                        <TableCell>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {safeMap(configs, (config) => (
                        <React.Fragment key={config.id}>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                {getStatusIcon(config)}
                              </Box>
                            </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">
                              {config.name}
                            </Typography>
                            {executing[config.id] && (
                              <LinearProgress sx={{ mt: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {/* ✅ MOSTRAR TÓPICO SI EXISTE */}
                            {config.topic ? (
                              <Box sx={{ mb: 1 }}>
                                <Chip
                                  label={`🎯 ${config.topic}`}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {safeMap(config.categories, (category) => (
                                  <Chip
                                    key={category.id}
                                    label={category.name}
                                    size="small"
                                    sx={{
                                      backgroundColor: category.color,
                                      color: "white",
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{config.publish_time}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label={config.tone}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={config.style}
                                size="small"
                                variant="outlined"
                              />
                              {/* ✅ Mostrar fuente de imágenes */}
                              {config.image_source && (
                                <Chip
                                  label={`🖼️ ${config.image_source === 'generate' ? 'IA' : 'Buscar'}`}
                                  size="small"
                                  variant="outlined"
                                  color={config.image_source === 'generate' ? 'primary' : 'secondary'}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ejecutar generación ahora">
                              <IconButton
                                onClick={() => handleExecuteNow(config)}
                                color="primary"
                                disabled={executing[config.id]}
                                size="small"
                              >
                                <PlayArrow />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={config.is_active ? "Pausar configuración" : "Activar configuración"}>
                              <IconButton
                                onClick={() => handleToggleActive(config)}
                                color={config.is_active ? "warning" : "success"}
                                size="small"
                              >
                                {config.is_active ? <Pause /> : <PlayArrow />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar configuración">
                              <IconButton
                                onClick={() => handleOpenDialog(config)}
                                color="primary"
                                size="small"
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar configuración">
                              <IconButton
                                onClick={() => handleDelete(config.id)}
                                color="error"
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {/* Admin Controls Row */}
                        <TableRow>
                          <TableCell colSpan={6} sx={{ bgcolor: 'background.default', py: 1 }}>
                            <AutoGenControls 
                              config={config} 
                              onSuccess={fetchConfigs}
                            />
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estadísticas Rápidas */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
                Resumen de Automatización
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h4" color="primary">
                    {safeArray(configs).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configuraciones
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" color="success.main">
                    {safeArray(configs).filter((c) => c.is_active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activas
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" color="info.main">
                    {safeArray(configs).filter((c) => c.topic).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Con Tópico
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" color="secondary.main">
                    {safeArray(configs).filter((c) => c.use_search).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Buscan en Web
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Schedule sx={{ mr: 1, verticalAlign: "middle" }} />
                Próxima Generación
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Sistema listo para ejecución automática
              </Typography>
              <Tooltip title="Ejecuta manualmente todas las configuraciones activas ahora">
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  size="medium"
                  onClick={() => {
                    // Ejecutar todas las configuraciones activas
                    configs.filter((c) => c.is_active).forEach(handleExecuteNow);
                  }}
                >
                  Ejecutar Todas las Activas
                </Button>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Debug Panel */}
        <Grid item xs={12}>
          <AutoGenDebugPanel configs={configs} />
        </Grid>
      </Grid>

      {/* Diálogo de Configuración */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingConfig
            ? "Editar Configuración"
            : "Nueva Configuración Automática"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Nombre de la Configuración"
                fullWidth
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Ej: Generación Diaria Tecnología"
              />
            </Grid>

            {/* ✅ NUEVO: Campo de Tópico Específico */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tópico Específico (Opcional)"
                name="topic"
                value={formData.topic || ''}
                onChange={(e) =>
                  setFormData({ ...formData, topic: e.target.value })
                }
                helperText="Si escribes un tema aquí (ej. 'Bitcoin', 'Salud Mental'), el sistema buscará noticias sobre esto específicamente. Si lo dejas vacío, usará las categorías seleccionadas abajo."
                variant="outlined"
                placeholder="Ej: Inteligencia Artificial, Criptomonedas, Salud Mental..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Categorías</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(formData.categories) ? formData.categories : []}
                  onChange={(e) => {
                    const val = e.target.value;
                    const newCategories = typeof val === 'string' ? val.split(',') : val;
                    setFormData({ ...formData, categories: newCategories });
                  }}
                  label="Categorías"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const category = categories.find((c) => c.id === value);
                        return category ? (
                          <Chip
                            key={value}
                            label={category.name}
                            size="small"
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {/* Opción "Cualquiera" para selección aleatoria */}
                  <MenuItem value="any">
                    <em>Cualquiera (Selección Aleatoria)</em>
                  </MenuItem>
                  {safeMap(categories, (category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {formData.topic 
                  ? "⚠️ El tópico específico tiene prioridad sobre las categorías seleccionadas"
                  : "Selecciona las categorías para generación automática"
                }
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Hora de Publicación"
                type="time"
                fullWidth
                value={formData.publish_time}
                onChange={(e) =>
                  setFormData({ ...formData, publish_time: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Máximo de Posts por Día"
                type="number"
                fullWidth
                value={formData.max_posts_per_day}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_posts_per_day: e.target.value,
                  })
                }
                inputProps={{ min: 1, max: 10 }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tono</InputLabel>
                <Select
                  value={formData.tone}
                  onChange={(e) =>
                    setFormData({ ...formData, tone: e.target.value })
                  }
                  label="Tono"
                >
                  {tones.map((tone) => (
                    <MenuItem key={tone.value} value={tone.value}>
                      {tone.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Estilo</InputLabel>
                <Select
                  value={formData.style}
                  onChange={(e) =>
                    setFormData({ ...formData, style: e.target.value })
                  }
                  label="Estilo"
                >
                  {styles.map((style) => (
                    <MenuItem key={style.value} value={style.value}>
                      {style.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Nicho</InputLabel>
                <Select
                  value={formData.niche}
                  onChange={(e) =>
                    setFormData({ ...formData, niche: e.target.value })
                  }
                  label="Nicho"
                >
                  {niches.map((niche) => (
                    <MenuItem key={niche.value} value={niche.value}>
                      {niche.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ✅ NUEVO: Switch Publicar automáticamente */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_publish}
                    onChange={(e) =>
                      setFormData({ ...formData, auto_publish: e.target.checked })
                    }
                  />
                }
                label="Publicar automáticamente al generar (Pasa de 'draft' a 'published')"
              />
            </Grid>

            {/* ✅ NUEVO: Switch Compartir en redes sociales */}
            <Grid item xs={12}>
              <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.share_on_social}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        share_on_social: e.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Compartir en Redes Sociales"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.use_search}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        use_search: e.target.checked,
                      })
                    }
                    color="secondary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>Buscar en Internet (Datos actualizados)</Typography>
                    <Chip label="Nuevo" size="small" color="success" sx={{ height: 20, fontSize: '0.6rem' }} />
                  </Box>
                }
                sx={{ mt: 1 }}
              />
              
              <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Fuente de Imágenes
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant={formData.image_source === 'generate' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setFormData({...formData, image_source: 'generate'})}
                    color="primary"
                  >
                    🤖 Generar (IA)
                  </Button>
                  <Button 
                    variant={formData.image_source === 'search' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setFormData({...formData, image_source: 'search'})}
                    color="secondary"
                  >
                    🔍 Buscar (Internet)
                  </Button>
                </Box>
              </Box>
            </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                }
                label="Configuración activa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {editingConfig ? "Actualizar" : "Crear"} Configuración
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoGenerationConfig;